/**
 * Destaque do termo buscado num texto, com a MESMA normalização do servidor.
 *
 * A busca de contatos (`GET /contatos?buscaTextual=`) compara `nome` e `codigo`
 * sem acento, sem caixa e sem nada que não seja letra ou dígito (`normalizarBusca`
 * + `normalizarColunaSql` no server). Destacar com `includes` cru erraria nos casos
 * que mais importam: "joao" casa "João" e "11 9876" casa "+55 (11) 98765-4321".
 *
 * Por isso o texto é normalizado caractere a caractere guardando de que índice do
 * ORIGINAL cada caractere normalizado veio; o casamento acontece no normalizado e
 * o trecho destacado é devolvido em coordenadas do original — pontuação e espaços
 * no meio do casamento entram no destaque, como o usuário espera ver.
 */

export type TrechoDestacado = { texto: string; destaque: boolean };

/** Espelho de `normalizarBusca` do server. */
export function normalizarTermoBusca(termo: string): string {
    return (termo ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^\p{L}\p{N}]/gu, '')
        .toLowerCase();
}

/**
 * Normaliza `texto` lembrando a origem de cada caractere.
 * `origem[i]` = índice, no original, do caractere `normalizado[i]`.
 */
function normalizarComOrigem(texto: string): { normalizado: string; origem: number[] } {
    let normalizado = '';
    const origem: number[] = [];

    // Por code point (não por unidade UTF-16): `for…of` não parte emoji ao meio.
    let indice = 0;
    for (const caractere of texto) {
        const limpo = normalizarTermoBusca(caractere);
        for (const c of limpo) {
            normalizado += c;
            origem.push(indice);
        }
        indice += caractere.length;
    }

    return { normalizado, origem };
}

/**
 * Parte `texto` em trechos com e sem destaque para cada ocorrência do termo.
 * Termo vazio (ou que normaliza para vazio) devolve o texto inteiro sem destaque.
 */
export function destacarBusca(texto: string | null | undefined, termo: string | null | undefined): TrechoDestacado[] {
    const original = texto ?? '';
    const alvo = normalizarTermoBusca(termo ?? '');
    if (!original) return [];
    if (!alvo) return [{ texto: original, destaque: false }];

    const { normalizado, origem } = normalizarComOrigem(original);

    const intervalos: [number, number][] = [];
    let desde = 0;
    while (desde <= normalizado.length - alvo.length) {
        const achado = normalizado.indexOf(alvo, desde);
        if (achado < 0) break;

        const inicio = origem[achado];
        const ultimo = origem[achado + alvo.length - 1];
        // Fim exclusivo no original: inclui o code point inteiro do último caractere.
        const fim = ultimo + (original.codePointAt(ultimo)! > 0xffff ? 2 : 1);
        intervalos.push([inicio, fim]);

        desde = achado + alvo.length;
    }

    if (!intervalos.length) return [{ texto: original, destaque: false }];

    const trechos: TrechoDestacado[] = [];
    let cursor = 0;
    for (const [inicio, fim] of intervalos) {
        if (inicio > cursor) trechos.push({ texto: original.slice(cursor, inicio), destaque: false });
        trechos.push({ texto: original.slice(inicio, fim), destaque: true });
        cursor = fim;
    }
    if (cursor < original.length) trechos.push({ texto: original.slice(cursor), destaque: false });

    return trechos;
}
